import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailPaymentMethodsTooltip } from "../../../constants/TooltipInformation";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailPaymentMethod = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [paymentMethodName, setPaymentMethodName] = useState<string | null>(
    null,
  );
  const [active, setActive] = useState<boolean>(true);
  const [requireReceipts, setRequireReceipts] = useState<boolean>(false);
  const [numberOfReceipts, setNumberOfReceipts] = useState<string | null>(null);
  const [requireComment, setRequireComment] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [numberOfReceiptsErr, setNumberOfReceiptsErr] =
    useState<boolean>(false);
  const [retailPaymentMethods, setRetailPaymentMethods] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Payment Method Name": paymentMethodName,
    Active: active ? "Yes" : "No",
    "Require Receipts": requireReceipts ? "Yes" : "No",
    "# of Receipts": numberOfReceipts ?? "",
    "Require Comment": requireComment ? "Yes" : "No",
  };

  const validateInput = () => {
    setNameError(false);
    setNumberOfReceiptsErr(false);
    if (
      paymentMethodName &&
      !validateString(
        paymentMethodName,
        validationLength.paymentName,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return false;
    }
    if (requireReceipts) {
      if (!numberOfReceipts || numberOfReceipts === "") {
        setAlertMessage(
          "# of Receipts is mandatory when Require Receipts is Yes",
        );
        setAlertType("error");
        setOpenAlert(true);
        return false;
      }
    }
    if (
      numberOfReceipts &&
      numberOfReceipts !== "" &&
      !/^\d+$/.test(numberOfReceipts)
    ) {
      setNumberOfReceiptsErr(true);
      return false;
    }
    return true;
  };

  const editRetailPaymentMethods = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setPaymentMethodName(null);
      setActive(true);
      setRequireReceipts(false);
      setNumberOfReceipts(null);
      setRequireComment(false);
      setNameError(false);
      setNumberOfReceiptsErr(false);
      return;
    }
    if (list !== null) {
      setEditClicked(true);
      if (list.id != null) {
        setSelectedId(list.id);
      } else {
        setSelectedId(null);
      }
    }
    setPaymentMethodName(list?.["Payment Method Name"] ?? null);
    setActive(list?.["Active"] === "Yes");
    setRequireReceipts(list?.["Require Receipts"] === "Yes");
    setNumberOfReceipts(
      list?.["# of Receipts"] != null && String(list["# of Receipts"]) !== ""
        ? String(list["# of Receipts"])
        : null,
    );
    setRequireComment(list?.["Require Comment"] === "Yes");
  };

  const updateRetailPaymentMethods = () => {
    if (!paymentMethodName || String(paymentMethodName).trim() === "") {
      setAlertMessage("Payment Method Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailPaymentMethods.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.PAYMENTMETHODNAME,
        paymentMethodName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setNumberOfReceiptsErr(false);
    setRetailPaymentMethods((prev: any) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailPaymentMethods(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${RetailPropertyEnum.paymentMethods} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailPaymentMethods = () => {
    if (!paymentMethodName || String(paymentMethodName).trim() === "") {
      setAlertMessage("Payment Method Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailPaymentMethods,
        DNDFieldsEnum.PAYMENTMETHODNAME,
        paymentMethodName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailPaymentMethods((prev: any) =>
      updateData(
        prev !== null && prev?.length
          ? [
              ...prev,
              {
                id: retailPaymentMethods.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailPaymentMethods(null);
    setEditClicked(false);
  };

  const deleteRetailPaymentMethods = (id: number) => {
    setRetailPaymentMethods(deleteItem(retailPaymentMethods, id));
  };

  useEffect(() => {
    if (fieldValues !== undefined && fieldValues !== null) {
      setRetailPaymentMethods(fieldValues);
    }
  }, []);

  useEffect(() => {
    data(retailPaymentMethods);
    if (editClicked) {
      setSelectedId(
        checkData(retailPaymentMethods, selectedId) ? selectedId : null,
      );
    }
  }, [retailPaymentMethods]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Payment Methods
              <Tooltip
                title={tooltipComponent(retailPaymentMethodsTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailPaymentMethods(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailPaymentMethods(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Payment Method Name
              <Tooltip
                title={tooltipComponent(
                  retailPaymentMethodsTooltip.paymentMethodName,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailPaymentMethodName"
              variant="outlined"
              placeholder="Payment Method Name"
              value={paymentMethodName ?? ""}
              helperText={nameError ? validationMessages.paymentName : ""}
              error={nameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPaymentMethodName(e.target.value || null);
                setNameError(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Active?
              <Tooltip
                title={tooltipComponent(retailPaymentMethodsTooltip.active)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label=""
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Require Receipts?
              <Tooltip
                title={tooltipComponent(
                  retailPaymentMethodsTooltip.requireReceipts,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={requireReceipts}
                  onChange={() => setRequireReceipts(!requireReceipts)}
                />
              }
              label=""
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              # of Receipts
              <Tooltip
                title={tooltipComponent(
                  retailPaymentMethodsTooltip.numberOfReceipts,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailPaymentMethodNumberOfReceipts"
              variant="outlined"
              placeholder="# of Receipts"
              value={numberOfReceipts ?? ""}
              helperText={numberOfReceiptsErr ? "Enter numbers only" : ""}
              error={numberOfReceiptsErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.replace(/\D/g, "");
                setNumberOfReceipts(v || null);
                setNumberOfReceiptsErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Require Comment?
              <Tooltip
                title={tooltipComponent(
                  retailPaymentMethodsTooltip.requireComment,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={requireComment}
                  onChange={() => setRequireComment(!requireComment)}
                />
              }
              label=""
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId
                  ? updateRetailPaymentMethods
                  : addRetailPaymentMethods
              }
              id="addRetailPaymentMethod"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Payment
              Method
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Payment Methods Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailPaymentMethods?.length ? (
                <DraggableItemList
                  itemData={retailPaymentMethods}
                  onOrderUpdate={(order) => setRetailPaymentMethods(order)}
                  onEdit={(info) => {
                    editRetailPaymentMethods(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailPaymentMethods(id)}
                  code={DNDFieldsEnum.PAYMENTMETHODNAME}
                  name={DNDFieldsEnum.ACTIVE}
                />
              ) : (
                <Typography variant="body2">
                  No Payment Methods Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailPaymentMethod;
